import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

export default function CGV() {
    return (
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="cgv-page">
            <Seo
                title="Conditions générales de vente · AGB · Foundation of Light"
                description="Conditions générales de vente (CGV / AGB) des soins énergétiques, ateliers et abonnements Foundation of Light. Droit allemand, tribunaux de Cologne."
                path="/cgv"
                noindex
            />
            <p className="text-xs uppercase tracking-widest text-primary mb-3">Allgemeine Geschäftsbedingungen · AGB</p>
            <h1 className="font-serif text-4xl md:text-5xl mb-6">Conditions générales de vente</h1>
            <p className="text-sm text-muted-foreground mb-12">Dernière mise à jour : février 2026</p>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">1. Champ d'application</h2>
                <p className="text-sm leading-relaxed">
                    Les présentes conditions générales de vente (« CGV ») régissent l'ensemble des prestations proposées sur le site
                    {" "}<strong>foundation-of-light.com</strong> (ci-après « le Site ») par Olivier Masurel, Einzelunternehmer, dont les coordonnées figurent aux
                    {" "}<Link to="/mentions-legales" className="underline decoration-primary/40 underline-offset-4">mentions légales</Link>.
                </p>
                <p className="text-sm leading-relaxed">
                    Le Site s'adresse à une clientèle internationale (particuliers majeurs). Toute commande implique l'acceptation pleine et entière des présentes CGV.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">2. Prestations proposées</h2>
                <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
                    <li>Séances individuelles de soin énergétique à distance (visioconférence Zoom)</li>
                    <li>Ateliers et évènements collectifs en ligne (méditations, cercles, transmissions)</li>
                    <li>Abonnements mensuels ou annuels donnant accès à la communauté privée et à des réductions</li>
                    <li>Accompagnements sur plusieurs séances</li>
                    <li>Bons cadeaux (soins offerts à un tiers)</li>
                </ul>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">3. Prix et TVA</h2>
                <p className="text-sm leading-relaxed">
                    Les prix sont indiqués en euros (€), toutes taxes comprises.
                    En application du § 19 UStG (statut Kleinunternehmer), <strong>aucune TVA n'est facturée</strong>.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">4. Paiement</h2>
                <p className="text-sm leading-relaxed">Trois modes de paiement sont proposés :</p>
                <ul className="text-sm leading-relaxed list-disc pl-6 space-y-1">
                    <li><strong>Carte bancaire</strong> via Stripe (paiement sécurisé, redirection)</li>
                    <li><strong>PayPal</strong> (paiement sécurisé, redirection)</li>
                    <li><strong>Virement SEPA</strong> — la place est réservée dès réception effective du virement</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-2">
                    Les abonnements sont prélevés automatiquement à la date d'échéance jusqu'à annulation par le client depuis son espace personnel.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">5. Droit de rétractation</h2>
                <p className="text-sm leading-relaxed">
                    Conformément à l'article § 312g Abs. 2 Nr. 9 BGB (transposition de la directive UE 2011/83), le droit de rétractation de 14 jours <strong>ne s'applique pas</strong>
                    {" "}aux prestations de service liées à une date ou une période déterminée (soins et ateliers programmés à date fixe).
                </p>
                <p className="text-sm leading-relaxed mt-2">
                    Une politique d'annulation dédiée s'applique — voir section suivante.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">6. Politique d'annulation et de remboursement</h2>

                <h3 className="font-medium mt-4">Soins individuels et ateliers</h3>
                <p className="text-sm leading-relaxed">
                    Le client peut annuler ou reporter sa séance <strong>jusqu'à 24 heures avant l'horaire prévu</strong>. Passé ce délai, la séance est due dans son intégralité et n'est pas remboursable.
                </p>

                <h3 className="font-medium mt-4">Abonnements et accompagnements</h3>
                <p className="text-sm leading-relaxed">
                    Le client peut annuler à tout moment depuis son espace personnel. L'annulation prend effet à la <strong>fin de la période en cours</strong> et empêche tout prélèvement ultérieur. Les périodes déjà entamées ne sont pas remboursées.
                </p>

                <h3 className="font-medium mt-4">Annulation à notre initiative</h3>
                <p className="text-sm leading-relaxed">
                    Si nous devons annuler un soin individuel et qu'aucune nouvelle date ne peut être convenue, la prestation est intégralement remboursée.
                    Si nous devons reporter un atelier, le remplacement du direct par le replay ne donne pas droit à remboursement, l'accès à l'enregistrement étant équivalent au service acheté.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">7. Nature des prestations</h2>
                <p className="text-sm leading-relaxed">
                    Les soins énergétiques et pratiques spirituelles proposés sur le Site sont des <strong>accompagnements de bien-être</strong>.
                    Un article dédié (article 8) précise leur nature non médicale, les engagements du client et le régime de responsabilité.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">8. Nature des prestations et responsabilité</h2>
                <p className="text-sm leading-relaxed">
                    Les soins énergétiques proposés par Olivier Masurel, que ce soit dans le cadre de séances
                    individuelles ou d'ateliers collectifs, s'inscrivent dans une démarche de{" "}
                    <strong>bien-être, de détente et d'accompagnement personnel</strong>.
                    Ils ne constituent ni un acte médical, ni un diagnostic, ni une prescription, ni un traitement
                    au sens des législations françaises et allemandes en vigueur, et ne sauraient se substituer
                    à un avis, un suivi ou un traitement médical, psychologique ou psychiatrique.
                </p>
                <p className="text-sm leading-relaxed">
                    Le client reconnaît avoir été informé de cette nature non médicale des prestations et s'engage
                    à consulter un médecin ou tout professionnel de santé qualifié pour toute question relative à
                    sa santé physique ou psychique, avant, pendant ou après les séances. Il lui appartient également
                    de signaler, <strong>avant toute séance</strong>, toute contre-indication ou situation particulière
                    (grossesse, épilepsie, trouble cardiaque, suivi psychiatrique en cours, etc.) qui pourrait
                    nécessiter une prudence particulière ou l'avis préalable d'un professionnel de santé.
                </p>
                <p className="text-sm leading-relaxed">
                    En cas de doute ou de besoin urgent lié à sa santé, le client s'engage à contacter sans délai
                    un médecin, un service d'urgence ou tout professionnel de santé compétent, les prestations
                    proposées ne pouvant en aucun cas remplacer une prise en charge médicale.
                </p>
                <p className="text-sm leading-relaxed">
                    Olivier Masurel met tout en œuvre pour offrir un accompagnement de qualité, dans un cadre
                    bienveillant et sécurisant, mais ne saurait être tenu responsable des conséquences d'une
                    absence de consultation médicale par le client, ni de tout dommage résultant d'une information
                    de santé incomplète ou inexacte communiquée par le client avant la séance.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">9. Propriété intellectuelle</h2>
                <p className="text-sm leading-relaxed">
                    L'ensemble des contenus (textes, enregistrements, images, méditations guidées, replays, supports pédagogiques) restent la propriété exclusive d'Olivier Masurel.
                    Toute reproduction, diffusion ou revente sans autorisation écrite est interdite.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">10. Données personnelles</h2>
                <p className="text-sm leading-relaxed">
                    Le traitement des données personnelles est décrit dans notre
                    {" "}<Link to="/confidentialite" className="underline decoration-primary/40 underline-offset-4">politique de confidentialité</Link> conforme au RGPD / DSGVO.
                </p>
            </section>

            <section className="space-y-3 mb-10">
                <h2 className="font-serif text-2xl mb-3">11. Droit applicable et juridiction</h2>
                <p className="text-sm leading-relaxed">
                    Les présentes CGV sont soumises au <strong>droit allemand</strong>. En cas de litige et à défaut de résolution amiable, les tribunaux compétents sont ceux de Cologne (Köln), Allemagne.
                </p>
                <p className="text-sm leading-relaxed mt-2">
                    Plateforme européenne de règlement en ligne des litiges :{" "}
                    <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="underline decoration-primary/40 underline-offset-4">https://ec.europa.eu/consumers/odr</a>
                </p>
            </section>

            <p className="text-xs text-muted-foreground mt-16">
                Voir aussi : <Link to="/mentions-legales" className="underline decoration-primary/40 underline-offset-4">Mentions légales</Link> · <Link to="/confidentialite" className="underline decoration-primary/40 underline-offset-4">Politique de confidentialité</Link>
            </p>
        </div>
    );
}
